import { type ReactElement, useState } from 'react';
import { Icon } from '../../Icon/Icon';
import { TAPlanStatus } from '../../TAPlan/TAPlan.types';
import { useBootstrapModalBlur } from '../../Util/useBootstrapModalBlur';
import { SidebarLayerControl } from '../Controls';
import { TAPLAN_SECTION } from '../Controls/SidebarLayerControl.types';
import { removeAllFilter } from '../LayerFactories';
import { taPlan } from '../LayerFactories/TAPlan';
import { ClusteredMarkerLayer } from '../Layers/ClusteredMarkersLayer';
import { FilterLayer } from '../Layers/FilterLayer';
import { LeafletMap } from '../Map';

import { type Feature } from 'geojson';
import type { FilterLayerOptions } from '../Layers/Layers.types';
import type { TAPlanOverviewPageMapProps } from './types';

const taPlanStatusFilter = function (id: string, status: TAPlanStatus, layer: ClusteredMarkerLayer, options?: FilterLayerOptions): FilterLayer {
    const defaultOptions: FilterLayerOptions = {
        filterFn: (filteredFeatures: Feature[], allFeatures: Feature[]) => {
            return allFeatures.filter((feature) => {
                const props = feature.properties;
                return filteredFeatures.includes(feature) || props.status === status;
            });
        },
    };

    return new FilterLayer(id, [layer], { ...defaultOptions, ...options });
};
const taPlanTypeFilter = function (id: string, trafficType: string, layer: ClusteredMarkerLayer, options?: FilterLayerOptions): FilterLayer {
    const defaultOptions: FilterLayerOptions = {
        filterFn: (filteredFeatures: Feature[]) => {
            filteredFeatures.forEach((feature) => {
                if (feature.properties.affectedTrafficTypes.includes(trafficType)) {
                    feature['includedInFilter'] = true;
                }
            });
            return filteredFeatures;
        },
    };

    return new FilterLayer(id, [layer], { ...defaultOptions, ...options });
};

const flagHackClear = function (layer: ClusteredMarkerLayer, options?: FilterLayerOptions): FilterLayer {
    const defaultOptions: FilterLayerOptions = {
        filterFn: (filteredFeatures: Feature[], allFeatures: Feature[]) => {
            allFeatures.forEach((feature) => {
                feature['includedInFilter'] = false;
            });
            return filteredFeatures;
        },
    };

    return new FilterLayer('clear-flag-filter', [layer], { ...defaultOptions, ...options });
};

const flagHackCheck = function (layer: ClusteredMarkerLayer, options?: FilterLayerOptions): FilterLayer {
    const defaultOptions: FilterLayerOptions = {
        filterFn: (filteredFeatures: Feature[]) => {
            return filteredFeatures.filter((feature) => feature['includedInFilter']);
        },
    };

    return new FilterLayer('check-flag-filter', [layer], { ...defaultOptions, ...options });
};

let layers;

const TAPlanSymbolsReference = (): ReactElement => {
    return (
        <>
            <h3>Avstängningar och omledningar</h3>
            <ul className="symbolList list-unstyled mb-0">
                <li className="d-flex">
                    <Icon className="icon" symbol="ta-plan-current" />
                    <span>Gällande tillstånd</span>
                </li>
                <li className="d-flex">
                    <Icon className="icon" symbol="ta-plan-coming" />
                    <span>Kommande tillstånd</span>
                </li>
                <li className="d-flex">
                    <Icon className="icon" symbol="ta-plan-completed" />
                    <span>Nyligen avslutade tillstånd</span>
                </li>
            </ul>
        </>
    );
};

export const TAPlanOverviewPageMap = (props: TAPlanOverviewPageMapProps): ReactElement => {
    const mapProps = props.mapSettings;

    const [map, setMap] = useState<L.Map>();

    useBootstrapModalBlur('taPlanSymbolsModal');

    const setupMap = (map: L.Map): void => {
        const taPlanLayer = taPlan().addTo(map);

        removeAllFilter([taPlanLayer], { order: 0 }).addTo(map);
        const filterComing = taPlanStatusFilter('taplan-coming', TAPlanStatus.Coming, taPlanLayer, { order: 1 }).addTo(map);
        const filterCurrent = taPlanStatusFilter('taplan-current', TAPlanStatus.Current, taPlanLayer, { order: 2 }).addTo(map);
        const filterCompleted = taPlanStatusFilter('taplan-completed', TAPlanStatus.RecentlyCompleted, taPlanLayer, { order: 3 });

        // Ok, this is quite a hack to make it possible to filter the already filtered markers on a second property.
        // As the second property (affectedTrafficTypes) can have multiple values we can't filter anything out until
        // it has been tested against all possible values. Therefore, instead of filtering out anything, we set a flag
        // on each marker that is matched by the current filter. The final filter checks all previous filtered markers
        // and removes all markers without the check.
        flagHackClear(taPlanLayer, { order: 4 }).addTo(map);
        const filterBike = taPlanTypeFilter('taplan-bike', 'cykel', taPlanLayer, { order: 5 }).addTo(map);
        const filterWalk = taPlanTypeFilter('taplan-walk', 'gång', taPlanLayer, { order: 6 }).addTo(map);
        const filterPT = taPlanTypeFilter('taplan-pt', 'kollektivtrafik', taPlanLayer, { order: 7 }).addTo(map);
        const filterMotor = taPlanTypeFilter('taplan-motor', 'motorfordon', taPlanLayer, { order: 8 }).addTo(map);
        flagHackCheck(taPlanLayer, { order: 9 }).addTo(map);

        layers = {
            'ta-plans': { layer: taPlanLayer, sections: [TAPLAN_SECTION] },
            'ta-plans-coming': { layer: filterComing, sections: [TAPLAN_SECTION] },
            'ta-plans-current': { layer: filterCurrent, sections: [TAPLAN_SECTION] },
            'ta-plans-completed': { layer: filterCompleted, sections: [TAPLAN_SECTION] },
            'ta-plans-bike': { layer: filterBike, sections: [TAPLAN_SECTION] },
            'ta-plans-walk': { layer: filterWalk, sections: [TAPLAN_SECTION] },
            'ta-plans-pt': { layer: filterPT, sections: [TAPLAN_SECTION] },
            'ta-plans-motor': { layer: filterMotor, sections: [TAPLAN_SECTION] },
        };

        setMap(map);
    };

    return (
        <>
            {map && (
                <SidebarLayerControl
                    map={map}
                    scope="ta-plans"
                    region={mapProps.region.toLowerCase()}
                    layers={layers}
                    defaultOnLayers={[]}
                    sectionsToShow={[TAPLAN_SECTION]}
                    defaultSection={TAPLAN_SECTION}
                    sectionHeaders={{ [TAPLAN_SECTION]: props.taPlanHeading ?? 'TA-planer' }}
                    roadHelpElementId="taPlanSymbolsModal"
                    taPlanDescription={props.taPlanDescription}
                    taPlanContact={props.taPlanContact}
                />
            )}
            <LeafletMap {...mapProps} disableFullscreenControl={true} scrollWheelZoom={true} startOnFeatureBounds={true} onMapInitiated={setupMap} />
            <div
                className="modal fade"
                id="taPlanSymbolsModal"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="taPlanSymbolsModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title" id="taPlanSymbolsModalLabel">
                                Teckenförklaring
                            </h2>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Stäng"></button>
                        </div>
                        <div className="modal-body">
                            <TAPlanSymbolsReference />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
